import { ActionIcon, Button, Paper, TextInput } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import React, { useEffect, useState } from "react";
import { getMyProfile, linkWhatsAppNumber } from "../apis/userApis";
import { getCookiesSession, setCookiesSession } from "../cookiesSession";

// Self-service profile screen: lets a user link their WhatsApp number so the
// Evidence Capture bot can resolve inbound messages back to their account.
const MyProfile = () => {
  const cookieData = JSON.parse(getCookiesSession("LawSignaUserDataMaster") || "{}");
  const storedUser = cookieData.user || {};
  const [savedPhone, setSavedPhone] = useState(storedUser.phone || "");
  const [phone, setPhone] = useState(storedUser.phone || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(!storedUser.phone);

  useEffect(() => {
    const loadPhone = async () => {
      try {
        const response = await getMyProfile();
        if (response.success) {
          const realPhone = response.user.phone || "";
          setSavedPhone(realPhone);
          setPhone(realPhone);
          setIsEditing(!realPhone);
        }
      } catch (e) {
        // Keep whatever the cookie already had - not worth blocking the page.
      }
    };
    loadPhone();
  }, []);

  const savePhone = async () => {
    let digitsOnly = phone.replace(/\D/g, "");
    if (digitsOnly && digitsOnly.length < 10) {
      setError("Enter a valid WhatsApp number, including country code.");
      return;
    }
    // WhatsApp's webhook always sends the sender's number with country code
    // included (e.g. "919876543210") - a bare 10-digit local number would
    // never match, so normalize it to a +91 number rather than silently
    // saving something that can never resolve an inbound message.
    if (digitsOnly.length === 10) {
      digitsOnly = `91${digitsOnly}`;
    }
    setError("");
    setSaving(true);
    try {
      const response = await linkWhatsAppNumber({ phone: digitsOnly });
      if (response.success) {
        const updatedUser = { ...storedUser, phone: digitsOnly };
        setCookiesSession(
          "LawSignaUserDataMaster",
          JSON.stringify({ ...cookieData, user: updatedUser }),
          2
        );
        setPhone(digitsOnly);
        setSavedPhone(digitsOnly);
        setIsEditing(false);
        showNotification({
          color: "green",
          title: "Profile updated",
          message: "Your WhatsApp number has been linked.",
        });
      } else {
        showNotification({
          color: "red",
          title: "Update failed",
          message: response.error || "Please try again.",
        });
      }
    } catch (e) {
      showNotification({
        color: "red",
        title: "Update failed",
        message: "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4">
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <h1 className="dashboard-heading">My Profile</h1>
          <p className="dashboard-subtitle">
            Manage your account details and link WhatsApp for evidence submission
          </p>
        </div>
      </div>

      <Paper withBorder radius="md" p="lg" style={{ maxWidth: 640 }}>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <TextInput label="Name" value={storedUser.name || ""} disabled />
          <TextInput label="Email" value={storedUser.email || ""} disabled />
        </div>
        <TextInput
          label="WhatsApp Number"
          placeholder="e.g. 919876543210"
          description="Include country code, no spaces or symbols. Used to submit evidence via WhatsApp."
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={error}
          disabled={!isEditing}
          className="mb-4"
          rightSection={
            !isEditing && (
              <ActionIcon onClick={() => setIsEditing(true)}>
                <IconPencil size={16} />
              </ActionIcon>
            )
          }
        />
        {isEditing && (
          <div className="flex gap-2">
            <Button onClick={savePhone} loading={saving}>
              Save
            </Button>
            {savedPhone && (
              <Button
                variant="default"
                onClick={() => {
                  setPhone(savedPhone);
                  setError("");
                  setIsEditing(false);
                }}
              >
                Cancel
              </Button>
            )}
          </div>
        )}
      </Paper>
    </div>
  );
};

export default MyProfile;
